/**
 * Locale-driven formatting (TMMAPP-0001). Money arrives as integer minor units and is rendered
 * with Intl in the caller's locale; civil dates (`YYYY-MM-DD`) render as received and are never
 * zone-shifted; instants arrive as ISO-8601 UTC and render in the caller's time zone.
 */

/** The fraction digits of an amount with no served currency: the usual two (TMMAPP-0105 §C 9). */
const UNKNOWN_CURRENCY_DIGITS = 2;

/**
 * A currency's minor-unit digits, from Intl's own knowledge of the currency (USD → 2, JPY → 0).
 * With no currency served (`null`), the usual two.
 */
export function minorUnitDigits(currency: string | null | undefined, locale: string): number {
  if (currency === null || currency === undefined || currency === '')
    return UNKNOWN_CURRENCY_DIGITS;
  const digits = new Intl.NumberFormat(locale, { style: 'currency', currency }).resolvedOptions()
    .maximumFractionDigits;
  // The lib types `maximumFractionDigits` as optional (it is absent for some non-currency
  // notations); a currency format always resolves it, so an absence is a malformed input.
  if (digits === undefined) throw new Error(`Cannot resolve the minor unit of: ${currency}`);
  return digits;
}

/** Divisor for a currency's minor unit (JPY → 1). */
function minorUnitDivisor(locale: string, currency: string | null | undefined): number {
  return 10 ** minorUnitDigits(currency, locale);
}

/**
 * U+2212 MINUS SIGN (contract v2 §13, TMMAPP-0154 §A 1): every negative amount or number renders
 * with it, never the hyphen-minus Intl gives `en` / `es` / `fr` / `pt`. Swapped here, on the
 * formatter's own `minusSign` part, never by string replacement in a component.
 */
export const TRUE_MINUS = '\u2212';

/** The options an amount's formatters share (TMMAPP-0154 §A 2). */
export interface MoneyFormatOptions {
  /**
   * Whole units, for an aggregate (contract v2 §13: net worth, Overview tiles, section totals,
   * targets, budgets, goals, Home's glance): no fraction digits, rounded half away from zero as Intl
   * does. An account's or envelope's own balance, a register, a plan line and a recurring amount keep
   * the currency's cents.
   */
  whole?: boolean;
}

/** A formatter's parts with the true minus in place of Intl's hyphen-minus. */
function withTrueMinus(parts: Intl.NumberFormatPart[]): Intl.NumberFormatPart[] {
  return parts.map((part) => (part.type === 'minusSign' ? { ...part, value: TRUE_MINUS } : part));
}

function joinParts(parts: Intl.NumberFormatPart[]): string {
  return parts.map((part) => part.value).join('');
}

/** The formatter and the number an amount in minor units renders through. */
function moneyFormat(
  minor: number,
  currency: string | null | undefined,
  locale: string,
  signed: boolean,
  { whole = false }: MoneyFormatOptions,
): { format: Intl.NumberFormat; amount: number } {
  const noCurrency = currency === null || currency === undefined || currency === '';
  const digits = whole ? { minimumFractionDigits: 0, maximumFractionDigits: 0 } : {};
  const signDisplay = signed ? ('exceptZero' as const) : ('auto' as const);
  const format = noCurrency
    ? new Intl.NumberFormat(locale, {
        minimumFractionDigits: UNKNOWN_CURRENCY_DIGITS,
        maximumFractionDigits: UNKNOWN_CURRENCY_DIGITS,
        ...digits,
        signDisplay,
      })
    : new Intl.NumberFormat(locale, { style: 'currency', currency, ...digits, signDisplay });
  const exact = noCurrency
    ? minor / 10 ** UNKNOWN_CURRENCY_DIGITS
    : minor / minorUnitDivisor(locale, currency);
  // An amount that rounds to nothing in whole units reads "$0", never "−$0"; and -0 is 0.
  const amount = exact === 0 || (whole && Math.abs(exact) < 0.5) ? 0 : exact;
  return { format, amount };
}

/**
 * An amount as a plain number with two fraction digits and no symbol — what an amount renders as
 * when no currency is served (`formatMinorPlain(-123456, 'en')` → `−1,234.56`). Never a guessed
 * currency (TMMAPP-0105 §C 9).
 */
export function formatMinorPlain(
  minor: number,
  locale: string,
  signed = false,
  options: MoneyFormatOptions = {},
): string {
  return formatMoneyMinor(minor, null, locale, signed, options);
}

/**
 * `formatMoneyMinor(-123456, 'USD', 'en')` → `−$1,234.56` (a true minus), `{ whole: true }` →
 * `−$1,235`; with no currency, the plain number (`formatMinorPlain`).
 */
export function formatMoneyMinor(
  minor: number,
  currency: string | null | undefined,
  locale: string,
  signed = false,
  options: MoneyFormatOptions = {},
): string {
  const { format, amount } = moneyFormat(minor, currency, locale, signed, options);
  return joinParts(withTrueMinus(format.formatToParts(amount)));
}

/**
 * The same amount as `formatMoneyMinor`, in its parts, with the sign kept apart (TMMAPP-0111, the 0108
 * review): `MoneyText` sets the digits tabular and the sign proportional, so "−$2,424.30" reads tight
 * while columns still align. `sign` marks a `minusSign` / `plusSign` part.
 */
export function formatMoneyMinorParts(
  minor: number,
  currency: string | null | undefined,
  locale: string,
  signed = false,
  options: MoneyFormatOptions = {},
): { text: string; sign: boolean }[] {
  const { format, amount } = moneyFormat(minor, currency, locale, signed, options);
  return withTrueMinus(format.formatToParts(amount)).map((part) => ({
    text: part.value,
    sign: part.type === 'minusSign' || part.type === 'plusSign',
  }));
}

/** `currencyDisplayName('USD', 'en')` → `US Dollar` (Intl.DisplayNames); the code if unknown. */
export function currencyDisplayName(currency: string, locale: string): string {
  try {
    return new Intl.DisplayNames(locale, { type: 'currency' }).of(currency) ?? currency;
  } catch {
    return currency;
  }
}

/** The locale's group and decimal separators, read from Intl rather than assumed. */
function separators(locale: string): { group: string; decimal: string } {
  const parts = new Intl.NumberFormat(locale).formatToParts(12345.6);
  return {
    group: parts.find((p) => p.type === 'group')?.value ?? ',',
    decimal: parts.find((p) => p.type === 'decimal')?.value ?? '.',
  };
}

interface ParsedDecimal {
  negative: boolean;
  integer: string;
  fraction: string;
}

/**
 * A typed decimal in the locale's separators: an optional leading `-` (or `−`), any non-numeric noise
 * (a currency symbol / code, `%`, spaces) stripped, groups dropped. `null` when nothing numeric
 * remains, a `-` sits anywhere but first, or there is more than one decimal separator.
 */
function parseLocaleDecimal(input: string, locale: string): ParsedDecimal | null {
  const { group, decimal } = separators(locale);
  let cleaned = '';
  for (const typed of input) {
    // A pasted amount may carry the true minus the app renders.
    const ch = typed === TRUE_MINUS ? '-' : typed;
    if (/\d/.test(ch) || ch === '-' || ch === group || ch === decimal) cleaned += ch;
  }
  if (cleaned.length === 0) return null;
  const negative = cleaned.startsWith('-');
  if (negative) cleaned = cleaned.slice(1);
  if (cleaned.includes('-')) return null;
  cleaned = cleaned.split(group).join('');
  const pieces = cleaned.split(decimal);
  if (pieces.length > 2) return null;
  const integer = pieces[0] ?? '';
  const fraction = pieces[1] ?? '';
  if (integer.length === 0 && fraction.length === 0) return null;
  return { negative, integer: integer.length === 0 ? '0' : integer, fraction };
}

/**
 * `parseMoneyToMinor('1,234.56', 'USD', 'en')` → `123456`; `('-$50', 'USD', 'en')` → `-5000`;
 * `('1.234,5', 'EUR', 'es')` → `123450`. `null` when unparseable or when more fraction digits
 * than the currency allows are given.
 */
export function parseMoneyToMinor(
  input: string,
  currency: string | null | undefined,
  locale: string,
): number | null {
  const parsed = parseLocaleDecimal(input, locale);
  if (parsed === null) return null;
  const digits = minorUnitDigits(currency, locale);
  if (parsed.fraction.length > digits) return null;
  const minor = Number(parsed.integer + parsed.fraction.padEnd(digits, '0'));
  if (!Number.isSafeInteger(minor)) return null;
  return parsed.negative ? -minor : minor;
}

/** Locale number; callers set fraction digits (FX rates use up to 6). */
export function formatNumber(
  value: number | string,
  locale: string,
  options: Intl.NumberFormatOptions = {},
): string {
  // Intl accepts decimal strings without first losing precision through Number conversion.
  return joinParts(
    withTrueMinus(new Intl.NumberFormat(locale, options).formatToParts(value as number)),
  );
}

const BYTES_PER_KILOBYTE = 1024;
const BYTES_PER_MEGABYTE = BYTES_PER_KILOBYTE * 1024;

/** `formatByteSize(300_000, 'en')` → `293 kB`; `(2_621_440, 'fr')` → `2,5 Mo` (Intl units). */
export function formatByteSize(bytes: number, locale: string): string {
  const kilobytes = bytes / BYTES_PER_KILOBYTE;
  // Pick the unit on the value as displayed (one fraction digit), so 1,047,999 B reads `1 MB`.
  const [value, unit] =
    Math.round(kilobytes * 10) / 10 >= BYTES_PER_KILOBYTE
      ? [bytes / BYTES_PER_MEGABYTE, 'megabyte']
      : [kilobytes, 'kilobyte'];
  return new Intl.NumberFormat(locale, {
    style: 'unit',
    unit,
    unitDisplay: 'short',
    maximumFractionDigits: 1,
  }).format(value);
}

/**
 * A FRACTION as the locale's percent (`formatPercent(0.2, 'en')` → `20%`, `('fr')` → `20 %`);
 * the caller passes the fraction (0.2), never 20. Whole percents unless `options` say otherwise.
 */
export function formatPercent(
  fraction: number,
  locale: string,
  options: Intl.NumberFormatOptions = {},
): string {
  return joinParts(
    withTrueMinus(
      new Intl.NumberFormat(locale, {
        maximumFractionDigits: 0,
        ...options,
        style: 'percent',
      }).formatToParts(fraction),
    ),
  );
}

/**
 * A percent served as text (pay-api's `ratePercent`, TMMAPP-0119 item 10), for display only: a bare
 * number is the locale's percent (`'7.25'` → `7.25%`, fr `7,25 %`); any other text renders verbatim.
 */
export function formatPercentText(value: string, locale: string): string {
  const raw = value.trim();
  if (!/^\d+(\.\d+)?$/.test(raw)) return raw;
  return formatPercent(Number(raw) / 100, locale, { maximumFractionDigits: 4 });
}

/** `bpToPercentText(4200, 'en')` → `42%` (basis points may exceed 10000 — `12,000%` renders as is). */
export function bpToPercentText(bp: number, locale: string): string {
  return formatPercent(bp / 10000, locale);
}

/**
 * A money amount as the text of an editable field: the locale's decimal separator, the currency's
 * minor-unit digits, no grouping — so `parseMoneyToMinor` reads it back unchanged
 * (`minorToInputText(1250, 'USD', 'es')` → `12,50`).
 */
export function minorToInputText(
  minor: number,
  currency: string | null | undefined,
  locale: string,
): string {
  const digits = minorUnitDigits(currency, locale);
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
    useGrouping: false,
  }).format(minor / 10 ** digits);
}

/** Basis points as an editable percent's text (`bpToInputText(425, 'fr')` → `4,25`), no grouping; `percentToBp` reads it back. */
export function bpToInputText(bp: number, locale: string): string {
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 2, useGrouping: false }).format(
    bp / 100,
  );
}

/** `percentToBp('4.25', 'en')` → `425` (two decimals at most; `null` otherwise or when unparseable). */
export function percentToBp(input: string, locale: string): number | null {
  const parsed = parseLocaleDecimal(input, locale);
  if (parsed === null || parsed.fraction.length > 2) return null;
  const bp = Number(parsed.integer + parsed.fraction.padEnd(2, '0'));
  if (!Number.isSafeInteger(bp)) return null;
  return parsed.negative ? -bp : bp;
}

/**
 * A civil date string (`YYYY-MM-DD`) rendered in the locale WITHOUT time-zone shifting: the parts
 * are read into a local-calendar Date (never `new Date('YYYY-MM-DD')`, which parses as UTC and
 * can render the previous day west of Greenwich). Throws on a malformed input.
 */
export function formatCivilDate(
  civil: string,
  locale: string,
  options: Intl.DateTimeFormatOptions = { dateStyle: 'medium' },
): string {
  return new Intl.DateTimeFormat(locale, options).format(civilLocalDate(civil));
}

/** A civil date's parts in a local-calendar Date; throws on a malformed or impossible date. */
function civilLocalDate(civil: string): Date {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(civil);
  if (match === null) throw new Error(`Not a civil date (YYYY-MM-DD): ${civil}`);
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    throw new Error(`Not a real calendar date: ${civil}`);
  }
  return date;
}

/**
 * A civil date that leaves the year out when it's the viewer's current year — "Oct 25", else
 * "Oct 25, 2027" (visual contract v2 §13; TMMAPP-0155 item 8). `today` is the viewer's civil today
 * (`todayCivilInZone(zone)`), so the year is the viewer's, never the device's. Parsed as
 * `formatCivilDate` parses, never with `new Date('YYYY-MM-DD')`.
 */
export function formatCivilDateNear(civil: string, locale: string, today: string): string {
  const sameYear = civil.slice(0, 4) === today.slice(0, 4);
  return formatCivilDate(
    civil,
    locale,
    sameYear ? { month: 'short', day: 'numeric' } : { dateStyle: 'medium' },
  );
}

/**
 * A civil date range through Intl's `formatRange`, so what both ends share prints once in the
 * locale's own way: "Apr 21 – Dec 30, 2025", "Oct 7 – 20". The year is left out when both ends are
 * in the viewer's current year, as `formatCivilDateNear` does (visual contract v2 §13); a range
 * that crosses years names both ("Dec 30, 2025 – Jan 4, 2026"). TMMAPP-0162 item 6.
 */
export function formatCivilRangeNear(
  from: string,
  to: string,
  locale: string,
  today: string,
): string {
  const year = today.slice(0, 4);
  const current = from.slice(0, 4) === year && to.slice(0, 4) === year;
  return new Intl.DateTimeFormat(
    locale,
    current ? { month: 'short', day: 'numeric' } : { dateStyle: 'medium' },
  ).formatRange(civilLocalDate(from), civilLocalDate(to));
}

/**
 * A civil month (`YYYY-MM`) rendered in the locale. The parts are read into a local-calendar
 * Date (never `new Date('YYYY-MM')` or `new Date('YYYY-MM-DD')`). Throws on a malformed input.
 */
export function formatCivilMonth(month: string, locale: string): string {
  const match = /^(\d{4})-(\d{2})$/.exec(month);
  if (match === null) throw new Error(`Not a civil month (YYYY-MM): ${month}`);
  const year = Number(match[1]);
  const monthIndex = Number(match[2]);
  const date = new Date(year, monthIndex - 1, 1);
  if (date.getFullYear() !== year || date.getMonth() !== monthIndex - 1) {
    throw new Error(`Not a real calendar month: ${month}`);
  }
  return new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(date);
}

/**
 * A civil month as a chart axis reads it (TMMAPP-0111, the 0108 review): the locale's short month and
 * a two-digit year marked with an apostrophe — "Oct '25". Same parsing rules as `formatCivilMonth`.
 */
export function formatCivilMonthShort(month: string, locale: string): string {
  formatCivilMonth(month, locale);
  const [year = 0, monthIndex = 1] = month.split('-').map(Number);
  return new Intl.DateTimeFormat(locale, { month: 'short', year: '2-digit' })
    .formatToParts(new Date(year, monthIndex - 1, 1))
    .map((part) => (part.type === 'year' ? `'${part.value}` : part.value))
    .join('');
}

/**
 * A civil month's short name alone (TMMAPP-0151 §G 21, a twelve-month chart's axis): "Oct". Same
 * parsing rules as `formatCivilMonth`.
 */
export function formatCivilMonthName(month: string, locale: string): string {
  formatCivilMonth(month, locale);
  const [year = 0, monthIndex = 1] = month.split('-').map(Number);
  return new Intl.DateTimeFormat(locale, { month: 'short' }).format(
    new Date(year, monthIndex - 1, 1),
  );
}

/** An ISO-8601 UTC instant rendered in the given IANA time zone. */
export function formatInstant(
  isoUtc: string,
  locale: string,
  timeZone: string,
  options: Intl.DateTimeFormatOptions = { dateStyle: 'medium', timeStyle: 'short' },
): string {
  const date = new Date(isoUtc);
  if (Number.isNaN(date.getTime())) throw new Error(`Not an instant: ${isoUtc}`);
  return new Intl.DateTimeFormat(locale, { ...options, timeZone }).format(date);
}

/** The short time-zone name of an instant in `timeZone` ("EDT", "GMT-6"), in the locale. */
export function formatZoneAbbreviation(isoUtc: string, locale: string, timeZone: string): string {
  const date = new Date(isoUtc);
  if (Number.isNaN(date.getTime())) throw new Error(`Not an instant: ${isoUtc}`);
  const parts = new Intl.DateTimeFormat(locale, {
    timeZone,
    timeStyle: 'long',
  }).formatToParts(date);
  return parts.find((part) => part.type === 'timeZoneName')?.value ?? timeZone;
}

/** A list of names joined by the locale's own rule ("A, B and C"). */
export function formatList(items: readonly string[], locale: string): string {
  return new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' }).format(items);
}

/**
 * An instant as `YYYY-MM-DD HH:mm` in `timeZone` (an audit timestamp, set in mono). The shape is
 * fixed, not the locale's: a log line reads the same in every language. The parts come from Intl,
 * so the person's zone and DST apply; `en-CA` is used only because it yields Latin digits.
 */
export function formatInstantStamp(isoUtc: string, timeZone: string): string {
  const date = new Date(isoUtc);
  if (Number.isNaN(date.getTime())) throw new Error(`Not an instant: ${isoUtc}`);
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')} ${get('hour')}:${get('minute')}`;
}

/**
 * How many civil days before `now` an instant falls in `timeZone`: `0` today, `1` yesterday, negative
 * ahead. A list row's relative time ("Today, 8:12 AM", "Yesterday") is built from it.
 */
export function instantDaysAgo(isoUtc: string, timeZone: string, now: Date = new Date()): number {
  const date = new Date(isoUtc);
  if (Number.isNaN(date.getTime())) throw new Error(`Not an instant: ${isoUtc}`);
  const civil = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const day = (value: Date) => Date.parse(`${civil.format(value)}T00:00:00Z`);
  return Math.round((day(now) - day(date)) / 86_400_000);
}

/** A day offset in the locale's own words: `0` "today", `1` "tomorrow", `2` "in 2 days". */
export function formatRelativeDays(days: number, locale: string): string {
  return new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(days, 'day');
}
